import { downloadReceipt } from "@api";
import {
  Badge,
  GridSection,
  GridSectionBody,
  GridSectionHeader,
  GridSectionItem,
  GridSectionSpan,
  Timeline,
} from "@components";
import CardNetworkIcon from "@components/visual/CardNetworkIcon";
import { formatAmount, formatDateTime } from "@utils";
import { useState } from "react";
import { hasFlag } from "country-flag-icons";
import Flags from "country-flag-icons/react/3x2";
import { BoxArrowUpRight, FileEarmarkPdf, CheckLg } from "react-bootstrap-icons";
import { PaperAirplaneIcon, ReceiptRefundIcon } from "@heroicons/react/24/outline";
import { transactionStatusColors, refundStatusLabel } from "../../../common";
import LedgerPayment from "../../LedgerPayment";
import SalesOrderPayment from "../../SalesOrderPayment";
import LinkedTransactionItem from "./LinkedTransactionItem";
import { reconciledTimelineEntry } from "./reconciledTimelineEntry";

const CountryFlag = ({ code }) => {
  if (!code || !hasFlag(code.toUpperCase())) return null;
  const Flag = Flags[code.toUpperCase()];
  return <Flag className="w-5 h-auto rounded-sm" />;
};

const regionNames = new Intl.DisplayNames(["en"], { type: "region" });
const countryName = (code) => {
  if (!code) return "—";
  try {
    return regionNames.of(code.toUpperCase());
  } catch {
    return code;
  }
};


// Builds the lifecycle activity feed for a payment: how it was asked for, when it was
// paid, any refunds raised against it, and whether it has been logged in the ERP.
// The Timeline sorts by date, so entries need only carry one.
const mapPaymentTimeline = (payment, reconciledLabel, reconciledByName) => {
  const events = [];

  if (payment.paymentRequest?.requestedOn) {
    events.push({
      title: "Payment requested",
      subTitle: [
        formatDateTime(payment.paymentRequest.requestedOn),
        payment.paymentRequest.requestedBy,
      ]
        .filter(Boolean)
        .join(" - "),
      date: payment.paymentRequest.requestedOn,
      nodeContent: { icon: PaperAirplaneIcon },
    });
  }

  if (payment.paymentDate) {
    events.push({
      title: "Paid",
      subTitle: formatDateTime(payment.paymentDate),
      amount: formatAmount(payment.amount),
      date: payment.paymentDate,
      nodeContent: { icon: CheckLg, color: "success" },
    });
  }

  // The refund's own lifecycle lives on its drawer; the payment only records that one was raised.
  (payment.refunds ?? []).forEach((refund) => {
    if (!refund.issuesOn) return;
    events.push({
      title: "Refund issued",
      subTitle: [formatDateTime(refund.issuesOn), refund.issuedBy]
        .filter(Boolean)
        .join(" - "),
      amount: formatAmount(refund.amount),
      date: refund.issuesOn,
      nodeContent: { icon: ReceiptRefundIcon },
    });
  });

  return [...events, ...reconciledTimelineEntry(reconciledLabel, reconciledByName)];
};

const PaymentDetails = ({ payment, paymentMethodKey, onNavigate, reconciledLabel, reconciledByName }) => {
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);

  const handleDownloadReceipt = async () => {
    setDownloadingReceipt(true);
    try {
      const response = await downloadReceipt(payment.id);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      window.URL.revokeObjectURL(url);
    } catch {
      // silently fail
    } finally {
      setDownloadingReceipt(false);
    }
  };

  const formatVia = () => {
    const method = payment.paymentRequest.communicationMethod;
    if (method === "Sms") {
      return (
        <span>
          SMS
          <span className="ms-2 fs-8 text-zinc-500">
            {payment.paymentRequest.recipientPhoneNumber}
          </span>
        </span>
      );
    }
    if (method === "Email") {
      return (
        <span>
          Email
          <span className="ms-2 fs-8 text-zinc-500">
            {payment.paymentRequest.recipientEmail}
          </span>
        </span>
      );
    }
    if (method === "SmsEmail") {
      return (
        <span>
          SMS
          <span className="ms-2 fs-8 text-zinc-500">
            {payment.paymentRequest.recipientPhoneNumber}
          </span>
          <br />
          Email
          <span className="ms-2 fs-8 text-zinc-500">
            {payment.paymentRequest.recipientEmail}
          </span>
        </span>
      );
    }
    if (method === "Link") {
      return "Link";
    }
    if (method === "QRCode") {
      return "QR Code";
    }
    return method;
  };

  return (
    <>
      <GridSection>
        <GridSectionSpan>
          <h2 className="text-3xl font-medium m-0 mr-2">
            {formatAmount(payment.amount)}
          </h2>
          <Badge color={transactionStatusColors[payment.status]}>
            {payment.status}
          </Badge>
        </GridSectionSpan>
        <GridSectionItem>
          <GridSectionHeader>Customer</GridSectionHeader>
          <GridSectionBody>{payment.customerName}</GridSectionBody>
        </GridSectionItem>
        <GridSectionItem>
          <GridSectionHeader>Payment reference</GridSectionHeader>
          <GridSectionBody>{payment.reference}</GridSectionBody>
        </GridSectionItem>
      </GridSection>
      <GridSection>
        <GridSectionItem>
          <GridSectionHeader>Source</GridSectionHeader>
          <GridSectionBody>{payment.source}</GridSectionBody>
        </GridSectionItem>
        {payment.paymentRequest && (
          <>
            <GridSectionItem>
              <GridSectionHeader>Channel</GridSectionHeader>
              <GridSectionBody>{formatVia()}</GridSectionBody>
            </GridSectionItem>
            <GridSectionItem>
              <GridSectionHeader>Requested by</GridSectionHeader>
              <GridSectionBody>
                {payment.paymentRequest.requestedBy}
              </GridSectionBody>
            </GridSectionItem>
            <GridSectionItem>
              <GridSectionHeader>Sent on</GridSectionHeader>
              <GridSectionBody>
                {formatDateTime(payment.paymentRequest.requestedOn)}
              </GridSectionBody>
            </GridSectionItem>
          </>
        )}
      </GridSection>
      <div className="w-full flex items-center px-5 pt-5">
        <p className="font-semibold m-0">Payment details</p>
      </div>
      <GridSection className="grid-cols-2">
        <GridSectionItem>
          <GridSectionHeader>Date paid</GridSectionHeader>
          <GridSectionBody>{formatDateTime(payment.paymentDate)}</GridSectionBody>
        </GridSectionItem>
        <GridSectionItem>
          <GridSectionHeader>Payment method</GridSectionHeader>
          <GridSectionBody>
            {payment?.corporateCardSurcharge != null
              ? "Corporate card"
              : payment.displayType}
          </GridSectionBody>
        </GridSectionItem>
        {payment?.corporateCardSurcharge != null && (
          <GridSectionItem>
            <GridSectionHeader>Card fee</GridSectionHeader>
            <GridSectionBody>
              {formatAmount(payment.corporateCardSurcharge)}
            </GridSectionBody>
          </GridSectionItem>
        )}
        {payment.terminalName && (
          <GridSectionItem>
            <GridSectionHeader>Terminal name</GridSectionHeader>
            <GridSectionBody>{payment.terminalName}</GridSectionBody>
          </GridSectionItem>
        )}
        {payment.terminalTransactionId && (
          <GridSectionItem>
            <GridSectionHeader>Terminal transaction ID</GridSectionHeader>
            <GridSectionBody>{payment.terminalTransactionId}</GridSectionBody>
          </GridSectionItem>
        )}
        <GridSectionItem>
          <GridSectionHeader>Payment confirmation</GridSectionHeader>
          <GridSectionBody>
            <button
              type="button"
              disabled={downloadingReceipt}
              onClick={handleDownloadReceipt}
              className="text-orange-500 hover:text-orange-600 font-medium flex items-center gap-1.5 transition-colors"
            >
              <FileEarmarkPdf className="w-4 h-4" />
              {downloadingReceipt ? "Downloading..." : "Download receipt"}
            </button>
          </GridSectionBody>
        </GridSectionItem>
        {payment.amountRefunded > 0 && (
          <GridSectionItem>
            <GridSectionHeader>Refund issued</GridSectionHeader>
            <GridSectionBody>
              {formatAmount(payment.amountRefunded)}
            </GridSectionBody>
          </GridSectionItem>
        )}
      </GridSection>
      {payment.cardDetails &&
        (payment.cardDetails.cardNetwork ||
          payment.cardDetails.last4Digits ||
          payment.cardDetails.expiryDate ||
          payment.cardDetails.issuerName ||
          payment.cardDetails.issuerCountry ||
          payment.cardDetails.threeDSecureOffered != null ||
          payment.cardDetails.threeDSecureAuthenticated != null ||
          payment.shopperIp) && (
          <>
            <div className="w-full flex items-center px-5 pt-5">
              <p className="font-semibold m-0">Card details</p>
            </div>
            <GridSection>
              {(payment.cardDetails.cardNetwork ||
                payment.cardDetails.last4Digits) && (
                <GridSectionItem>
                  <GridSectionHeader>Card</GridSectionHeader>
                  <GridSectionBody>
                    <span className="flex items-center gap-2">
                      {payment.cardDetails.cardNetwork && (
                        <CardNetworkIcon
                          network={payment.cardDetails.cardNetwork}
                        />
                      )}
                      {payment.cardDetails.last4Digits && (
                        <span>
                          &bull;&bull;&bull;&bull;{" "}
                          {payment.cardDetails.last4Digits}
                        </span>
                      )}
                    </span>
                  </GridSectionBody>
                </GridSectionItem>
              )}
              {payment.cardDetails.expiryDate && (
                <GridSectionItem>
                  <GridSectionHeader>Expiry</GridSectionHeader>
                  <GridSectionBody>
                    {payment.cardDetails.expiryDate.replace("/", " / ")}
                  </GridSectionBody>
                </GridSectionItem>
              )}
              {payment.cardDetails.issuerName && (
                <GridSectionItem>
                  <GridSectionHeader>Issuer Bank</GridSectionHeader>
                  <GridSectionBody>
                    {payment.cardDetails.issuerName}
                  </GridSectionBody>
                </GridSectionItem>
              )}
              {payment.cardDetails.issuerCountry && (
                <GridSectionItem>
                  <GridSectionHeader>Issuer country</GridSectionHeader>
                  <GridSectionBody>
                    <span className="flex items-center gap-2">
                      <CountryFlag code={payment.cardDetails.issuerCountry} />
                      <span>
                        {countryName(payment.cardDetails.issuerCountry)}
                      </span>
                    </span>
                  </GridSectionBody>
                </GridSectionItem>
              )}
              {payment.cardDetails.threeDSecureOffered != null && (
                <GridSectionItem>
                  <GridSectionHeader>3DS Requested</GridSectionHeader>
                  <GridSectionBody>
                    {payment.cardDetails.threeDSecureOffered ? "Yes" : "No"}
                  </GridSectionBody>
                </GridSectionItem>
              )}
              {payment.cardDetails.threeDSecureOffered &&
                payment.cardDetails.threeDSecureAuthenticated != null && (
                  <GridSectionItem>
                    <GridSectionHeader>3DS Successful</GridSectionHeader>
                    <GridSectionBody>
                      {payment.cardDetails.threeDSecureAuthenticated
                        ? "Yes"
                        : "No"}
                    </GridSectionBody>
                  </GridSectionItem>
                )}
              {payment.shopperIp && (
                <GridSectionItem>
                  <GridSectionHeader>Shopper IP</GridSectionHeader>
                  <GridSectionBody>
                    <span className="flex items-center gap-2">
                      <a
                        href={`https://ipapi.co/?q=${payment.shopperIp}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary font-medium flex items-center gap-2"
                      >
                        View details
                        <BoxArrowUpRight className="w-3 h-auto" />
                      </a>
                    </span>
                  </GridSectionBody>
                </GridSectionItem>
              )}
            </GridSection>
          </>
        )}
      <LedgerPayment
        payment={
          payment.ledgerPayment
            ? {
                ...payment.ledgerPayment,
                invoicesRequested: payment.invoicesRequested,
              }
            : null
        }
      />
      <SalesOrderPayment salesOrders={payment.salesOrders} />
      <div className="w-full flex items-center px-5 pt-5">
        <p className="font-semibold m-0">Linked refunds</p>
      </div>
      {payment.refunds && payment.refunds.length > 0 ? (
        payment.refunds
          .toSorted((a, b) => new Date(b.issuesOn) - new Date(a.issuesOn))
          .map((r) => (
          <LinkedTransactionItem
            key={r.id}
            reference={r.reference ?? "Refund"}
            linkLabel="View refund"
            icon={ReceiptRefundIcon}
            statusLabel={refundStatusLabel(r.status)}
            statusColor={transactionStatusColors[refundStatusLabel(r.status)]}
            amount={r.amount}
            date={r.issuesOn}
            onClick={
              onNavigate
                ? () =>
                    onNavigate({
                      transactionType: "Refund",
                      paymentId: payment.id,
                      refundId: r.id,
                      paymentMethodKey,
                    })
                : undefined
            }
          />
        ))
      ) : (
        <div className="px-5 py-3 text-sm text-zinc-500">
          No refunds have been issued for this payment.
        </div>
      )}
      <div className="border-t border-zinc-200">
        <Timeline activity={mapPaymentTimeline(payment, reconciledLabel, reconciledByName)} />
      </div>
    </>
  );
};

export default PaymentDetails;
